import { createAction, createReducer, isAnyOf } from '@reduxjs/toolkit';

import api from 'common/api';
import { TokenState } from 'common/api/token';
import {
  ClientFormValues,
  ClientVerificationFormEmailValues,
  ClientVerificationFormPhoneValues,
} from 'common/models/Forms';
import { AppState } from 'common/models/AppState';
import { TokenDeliveryMethod, TokenType } from 'common/models/Token';
import { selectIsPrivileged } from 'features/auth/ducks';
import {
  loadTransactionActions,
  selectForm,
  selectFormTransaction,
  selectInformationsCustomerInfo1,
  setInformationsClient1InfoValuesPartialAction,
} from 'features/form/ducks';

import { startListening, AppListenerEffectApi } from '../../../../root/store/listener-middleware';

export function selectClientVerification(state: AppState) {
  return selectForm(state).verification;
}

export const resetClientVerificationAction = createAction('@@Verification/RESET');

export const requestEmailVerificationCodeAction = createAction<{
  email: ClientVerificationFormEmailValues['email'];
}>('@@Verification/REQUEST_EMAIL_CODE');

export const requestPhoneVerificationCodeAction = createAction<{
  phone: ClientVerificationFormPhoneValues['phone'];
}>('@@Verification/REQUEST_SMS_CODE');

export const submitPhoneVerificationCodeAction = createAction<ClientVerificationFormPhoneValues['code']>(
  '@@Verification/SUBMIT_SMS_CODE',
);

export const submitEmailVerificationCodeAction = createAction<ClientVerificationFormEmailValues['code']>(
  '@@Verification/SUBMIT_EMAIL_CODE',
);

const changePhoneVerificationStateAction = createAction<TokenState>('@@Verification/CHANGE_SMS_VERIFICATION_STATE');

const changeEmailVerificationStateAction = createAction<TokenState>('@@Verification/CHANGE_EMAIL_VERIFICATION_STATE');

const updatePhoneVerificationStateAction = createAction<Partial<VerificationFormState['phone']>>(
  '@@Verification/UPDATE_SMS_VERIFICATION_STATE',
);

const updateEmailVerificationStateAction = createAction<Partial<VerificationFormState['email']>>(
  '@@Verification/UPDATE_EMAIL_VERIFICATION_STATE',
);

export interface VerificationFormState {
  phone: {
    attemptsLeft?: number;
    httpStatus?: number;
    status: TokenState;
  };
  email: {
    attemptsLeft?: number;
    httpStatus?: number;
    status: TokenState;
  };
}

export const initialVerificationFormState: VerificationFormState = {
  phone: {
    status: TokenState.DEFAULT,
  },
  email: {
    status: TokenState.DEFAULT,
  },
};

export default createReducer(initialVerificationFormState, (builder) => {
  builder
    .addCase(updatePhoneVerificationStateAction, (state, { payload }) => {
      Object.assign(state.phone, payload);
    })
    .addCase(updateEmailVerificationStateAction, (state, { payload }) => {
      Object.assign(state.email, payload);
    })
    .addCase(changeEmailVerificationStateAction, (state, { payload }) => {
      state.email = { status: payload };
    })
    .addCase(changePhoneVerificationStateAction, (state, { payload }) => {
      state.phone = { status: payload };
    })
    .addCase(resetClientVerificationAction, () => initialVerificationFormState);
});

// Listener effect factories
function createRequestVerificationCodeEffect(deliveryMethod: TokenDeliveryMethod) {
  return async (
    action:
      | ReturnType<typeof requestPhoneVerificationCodeAction>
      | ReturnType<typeof requestEmailVerificationCodeAction>,
    listenerApi: AppListenerEffectApi,
  ) => {
    listenerApi.cancelActiveListeners();
    listenerApi.dispatch(
      deliveryMethod === TokenDeliveryMethod.PHONE
        ? changePhoneVerificationStateAction(TokenState.REQUESTED)
        : changeEmailVerificationStateAction(TokenState.REQUESTED),
    );
    // Prevent accidental double-click submissions
    await listenerApi.delay(250);

    // Update the user data first, and only then ask for the code to be sent
    const isPrivileged = selectIsPrivileged(listenerApi.getState());

    if (!isPrivileged) {
      listenerApi.dispatch(setInformationsClient1InfoValuesPartialAction(action.payload as Partial<ClientFormValues>));
      await listenerApi.take(isAnyOf(loadTransactionActions.success, loadTransactionActions.failure));
    }

    const transaction = selectFormTransaction(listenerApi.getState());
    const { ok, status } = await api.token.create(transaction!._id, TokenType.VERIFICATION, deliveryMethod);
    const newState = {
      status: ok ? TokenState.DELIVERED : TokenState.DELIVERY_ERROR,
      httpStatus: status,
    };

    listenerApi.dispatch(
      deliveryMethod === TokenDeliveryMethod.PHONE
        ? updatePhoneVerificationStateAction(newState)
        : updateEmailVerificationStateAction(newState),
    );
  };
}

function createSubmitVerificationCodeEffect(deliveryMethod: TokenDeliveryMethod) {
  return async (action: ReturnType<typeof submitPhoneVerificationCodeAction>, listenerApi: AppListenerEffectApi) => {
    listenerApi.cancelActiveListeners();
    listenerApi.dispatch(
      deliveryMethod === TokenDeliveryMethod.PHONE
        ? changePhoneVerificationStateAction(TokenState.SUBMITTED)
        : changeEmailVerificationStateAction(TokenState.SUBMITTED),
    );

    const state = listenerApi.getState();
    const transaction = selectFormTransaction(state);
    const { phone, email } = selectInformationsCustomerInfo1(state)!;
    const { ok, status, response } = await api.token.verify(
      transaction!._id,
      TokenType.VERIFICATION,
      deliveryMethod,
      deliveryMethod === TokenDeliveryMethod.PHONE ? `${phone.prefix}${phone.number}` : email,
      action.payload,
    );
    const newState = {
      status: ok ? TokenState.VERIFIED : TokenState.SUBMISSION_ERROR,
      httpStatus: status,
      attemptsLeft: response?.data?.attemptsLeft,
    };

    listenerApi.dispatch(
      deliveryMethod === TokenDeliveryMethod.PHONE
        ? updatePhoneVerificationStateAction(newState)
        : updateEmailVerificationStateAction(newState),
    );

    // Load the transaction again to reflect the updated verification state set by BE
    listenerApi.dispatch(loadTransactionActions.request(transaction!._id));
  };
}

// Listeners
startListening({
  actionCreator: requestPhoneVerificationCodeAction,
  effect: createRequestVerificationCodeEffect(TokenDeliveryMethod.PHONE),
});

startListening({
  actionCreator: requestEmailVerificationCodeAction,
  effect: createRequestVerificationCodeEffect(TokenDeliveryMethod.EMAIL),
});

startListening({
  actionCreator: submitPhoneVerificationCodeAction,
  effect: createSubmitVerificationCodeEffect(TokenDeliveryMethod.PHONE),
});

startListening({
  actionCreator: submitEmailVerificationCodeAction,
  effect: createSubmitVerificationCodeEffect(TokenDeliveryMethod.EMAIL),
});
